import {
  HistoryOutlined,
  MailOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, message, Popover, Timeline, Tooltip } from 'antd';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { TooltipPlacement } from 'antd/lib/tooltip';
import React, { useState } from 'react';
import styled from 'styled-components';

import { fetchAPI } from '../../utils/api';
import { formatUTCTimeToLocal } from '../../utils/format';
import { GRNResponse } from '../GRN/types';

const OrderGRNReminder = (props: {
  record: any;
  onSuccess: () => void;
  onOpenChange?: (open: boolean) => void;
}) => {
  const [openTooltip, setOpenTooltip] = useState(false);
  const [loading, setLoading] = useState(false);

  const opsStatus = props.record?.operation_status;
  const grnData = useQuery<Partial<GRNResponse> & { loading: boolean }>(
    ['order-grn', props.record?.id, openTooltip],
    async () => {
      if (!openTooltip || !props.record?.id) {
        return {
          loading: false,
        };
      }

      try {
        const resp = await fetchAPI(`orders/${props.record?.id}/grn`);

        return resp;
      } catch (error) {
        message.error('Failed get GRN detail');
        if (error.message) {
          message.error(error.message);
        }
        return {
          loading: false,
        };
      }
    },
    {
      staleTime: 5 * 60 * 1000,
      placeholderData: {
        loading: true,
      },
    }
  );

  const sendGRNEmail = async () => {
    setLoading(true);
    try {
      await fetchAPI(`orders/${props.record?.id}/grn`, {
        method: 'POST',
      });
      await grnData.refetch();
      message.success('GRN Reminder Email sent!');
    } catch (error) {
      message.error('Failed send GRN Reminder email');
      if (error.message) {
        message.error(error.message);
      }
    }
    setLoading(false);
  };

  if (['REJECTED', 'COMPLETED', 'PO_CANCELLED'].includes(opsStatus)) {
    return null;
  }

  return (
    <Popover
      trigger={'click'}
      placement="rightTop"
      title="Remind GRN"
      visible={openTooltip}
      onVisibleChange={visible => {
        setOpenTooltip(visible);
        // Close dropdown immediately when popover is opened
        if (visible) {
          props.onOpenChange?.(false);
        }
      }}
      content={
        <Wrapper>
          <Button
            icon={<MailOutlined />}
            onClick={() => sendGRNEmail()}
            loading={loading}
          >
            Send GRN Reminder Email
          </Button>
          <GRNLogChanges
            logChanges={grnData.data?.log_changes || []}
            loading={loading}
          />
        </Wrapper>
      }
    >
      <Tooltip title="Remind GRN">
        <Button icon={<WarningOutlined />}>Remind GRN</Button>
      </Tooltip>
    </Popover>
  );
};

export const GRNLogChanges = (props: {
  logChanges?: GRNResponse['log_changes'];
  loading?: boolean;
  size?: SizeType;
  placement?: TooltipPlacement;
  style?: React.CSSProperties;
}) => {
  const [openTooltipHistory, setOpenTooltipHistory] = useState(false);
  return (
    <Popover
      trigger={'click'}
      placement={props.placement || 'rightBottom'}
      visible={openTooltipHistory}
      title="Trigger Email History"
      onVisibleChange={vis => setOpenTooltipHistory(vis)}
      content={
        <Timeline>
          {(props.logChanges || []).map((obj, idx) => (
            <Timeline.Item key={idx}>
              <TimelineWrapper>
                <p>
                  <b>{obj.username}</b>
                </p>
                <p>
                  at: {formatUTCTimeToLocal(obj.datetime, 'DD MMM YYYY, HH:mm')}
                </p>
              </TimelineWrapper>
            </Timeline.Item>
          ))}
        </Timeline>
      }
    >
      <Button
        icon={<HistoryOutlined />}
        disabled={props.loading}
        size={props.size || 'middle'}
        style={props.style || {}}
      >
        GRN Reminder Email History
      </Button>
    </Popover>
  );
};

const Wrapper = styled.div`
  > .ant-btn {
    display: block;
    margin-bottom: 15px;
  }
`;

const TimelineWrapper = styled.div`
  p {
    margin: 0;

    b {
      font-weight: 600;
    }
  }
`;

export default OrderGRNReminder;
